/**
 * WorkloadCalculatorModal - Interactive Workload Planning Tool
 * Two modes:
 * 1. Workload Planning - Simulate college combinations and MVV scenarios
 * 2. Custom Sprint Builder - Select specific tasks for today's session
 */

import React, { useState, useEffect } from 'react';
import { X, Calculator, Clock, Zap, TrendingUp, AlertTriangle, CheckCircle2, Target, PlayCircle, Calendar, ChevronDown } from 'lucide-react';
import DeadlineIntelligenceService from '../../services/DeadlineIntelligenceService';

/**
 * Filter timeline items to only include actionable tasks (not reminders or events)
 * Excludes: deadline_reminders, reminders, events, and items without workflow_key
 */
const getActionableTasks = (timelineItems) => {
  if (!timelineItems || !Array.isArray(timelineItems)) return [];

  return timelineItems.filter(item => {
    // Exclude deadline reminders - these are notifications, not work tasks
    if (item.source_type === 'deadline_reminder') return false;

    // Exclude other non-actionable types
    if (['reminder', 'event', 'college_deadline', 'deadline'].includes(item.source_type)) {
      return false;
    }

    // Only workflow-structured tasks are actionable
    if (!item.workflow_key) return false;

    // Exclude completed and skipped tasks (check all possible completion/skip fields)
    if (item.completion_status === 'completed' ||
        item.completion_status === 'skipped' ||
        item.completed ||
        item.status === 'completed' ||
        item.status === 'skipped') return false;

    return true;
  });
};

const WorkloadCalculatorModal = ({ isOpen, onClose, colleges = [], tasks = [], deadlineInfo, onStartSprint }) => {
  const [mode, setMode] = useState('planning'); // 'planning' or 'sprint'
  const [selectedColleges, setSelectedColleges] = useState(new Set());
  const [mvvSelections, setMVVSelections] = useState(new Set());
  const [selectedTasks, setSelectedTasks] = useState(new Set()); // For custom sprint
  const [taskMVVSelections, setTaskMVVSelections] = useState(new Set()); // Task-level MVV for sprint (tasks WITH MVV selected)
  const [expandedColleges, setExpandedColleges] = useState(new Set()); // Collapsed/expanded colleges
  const [expandedTaskDetails, setExpandedTaskDetails] = useState(new Set()); // Tasks with expanded MVV/Optimal details
  const [workloadResults, setWorkloadResults] = useState(null);
  const [availableHoursPerDay, setAvailableHoursPerDay] = useState(5);
  const [sprintDuration, setSprintDuration] = useState(120); // minutes

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('[WorkloadCalculatorModal] Colleges:', colleges);
      console.log('[WorkloadCalculatorModal] Sample college:', colleges[0]);
      console.log('[WorkloadCalculatorModal] Sample college.priority_order:', colleges[0]?.priority_order);
      console.log('[WorkloadCalculatorModal] Sample college.global_colleges:', colleges[0]?.global_colleges);
      console.log('[WorkloadCalculatorModal] All priority_orders:', colleges.map(c => ({ name: c.name || c.global_colleges?.name, priority: c.priority_order })));
      console.log('[WorkloadCalculatorModal] Tasks count:', tasks.length);
      const sampleTask = tasks.find(t => t.college_id);
      console.log('[WorkloadCalculatorModal] Sample task:', sampleTask);
      console.log('[WorkloadCalculatorModal] Sample task fields:', {
        id: sampleTask?.id,
        title: sampleTask?.title,
        estimated_duration: sampleTask?.estimated_duration,
        mvv_time_required: sampleTask?.mvv_time_required,
        min_viable_version: sampleTask?.min_viable_version,
        description: sampleTask?.description,
        college_id: sampleTask?.college_id
      });
      const taskWithMVV = tasks.find(t => t.min_viable_version || t.mvv_time_required);
      console.log('[WorkloadCalculatorModal] Task with MVV:', taskWithMVV);
      console.log('[WorkloadCalculatorModal] Tasks with min_viable_version count:', tasks.filter(t => t.min_viable_version).length);
      console.log('[WorkloadCalculatorModal] Tasks with mvv_time_required count:', tasks.filter(t => t.mvv_time_required).length);
    }
  }, [isOpen, colleges, tasks]);

  // Initialize with all colleges selected
  useEffect(() => {
    if (isOpen && colleges.length > 0) {
      setSelectedColleges(new Set(colleges.map(c => c.id)));
    }
  }, [isOpen, colleges]);

  // Calculate workload whenever selections change
  useEffect(() => {
    if (selectedColleges.size > 0) {
      calculateWorkload();
    }
  }, [selectedColleges, mvvSelections, availableHoursPerDay]);

  const calculateWorkload = () => {
    // Group tasks by college
    const collegeTasksMap = {};
    tasks.forEach(task => {
      if (task.college_id && selectedColleges.has(task.college_id)) {
        if (!collegeTasksMap[task.college_id]) {
          collegeTasksMap[task.college_id] = [];
        }
        collegeTasksMap[task.college_id].push(task);
      }
    });

    // Calculate hours for selected colleges
    const collegeBreakdowns = colleges
      .filter(c => selectedColleges.has(c.id))
      .map(college => {
        const collegeTasks = collegeTasksMap[college.id] || [];
        const incompleteTasks = collegeTasks.filter(t => t.completion_status !== 'completed');

        const useMVV = mvvSelections.has(college.id);

        const hours = incompleteTasks.reduce((sum, task) => {
          const time = useMVV && task.mvv_time_required
            ? task.mvv_time_required
            : task.estimated_duration || 0;
          return sum + (time / 60);
        }, 0);

        return {
          college,
          hours: Math.round(hours * 10) / 10,
          taskCount: incompleteTasks.length,
          useMVV
        };
      });

    const totalHours = collegeBreakdowns.reduce((sum, c) => sum + c.hours, 0);
    const daysUntil = deadlineInfo?.daysUntil || 30;
    const dailyHoursNeeded = Math.round((totalHours / daysUntil) * 10) / 10;
    const isFeasible = dailyHoursNeeded <= availableHoursPerDay;
    const bufferHours = (availableHoursPerDay * daysUntil) - totalHours;

    setWorkloadResults({
      colleges: collegeBreakdowns,
      totalHours: Math.round(totalHours * 10) / 10,
      daysUntil,
      dailyHoursNeeded,
      availableHoursPerDay,
      isFeasible,
      bufferHours: Math.round(bufferHours * 10) / 10,
      bufferPercentage: Math.round((bufferHours / (availableHoursPerDay * daysUntil)) * 100)
    });
  };

  const toggleCollege = (collegeId) => {
    const newSet = new Set(selectedColleges);
    if (newSet.has(collegeId)) {
      newSet.delete(collegeId);
      // Also remove from MVV selections
      const newMVV = new Set(mvvSelections);
      newMVV.delete(collegeId);
      setMVVSelections(newMVV);
    } else {
      newSet.add(collegeId);
    }
    setSelectedColleges(newSet);
  };

  const toggleMVV = (collegeId) => {
    const newSet = new Set(mvvSelections);
    if (newSet.has(collegeId)) {
      newSet.delete(collegeId);
    } else {
      newSet.add(collegeId);
    }
    setMVVSelections(newSet);
  };

  const toggleTask = (taskId) => {
    const newSet = new Set(selectedTasks);
    if (newSet.has(taskId)) {
      newSet.delete(taskId);
    } else {
      newSet.add(taskId);
    }
    setSelectedTasks(newSet);
  };

  const calculateSprintStats = () => {
    const selectedTasksList = tasks.filter(t => selectedTasks.has(t.id));
    const totalMinutes = selectedTasksList.reduce((sum, task) => {
      // Use MVV time if task has MVV selected, otherwise use standard time
      const useMVV = taskMVVSelections.has(task.id);
      const duration = useMVV && task.mvv_time_required
        ? task.mvv_time_required
        : (task.estimated_duration || 30);
      return sum + duration;
    }, 0);
    return {
      taskCount: selectedTasksList.length,
      totalMinutes: Math.round(totalMinutes),
      totalHours: Math.round(totalMinutes / 60 * 10) / 10,
      tasks: selectedTasksList.map(t => ({
        ...t,
        useMVV: taskMVVSelections.has(t.id)
      }))
    };
  };

  const toggleTaskMVV = (taskId) => {
    const newSet = new Set(taskMVVSelections);
    if (newSet.has(taskId)) {
      newSet.delete(taskId);
    } else {
      newSet.add(taskId);
    }
    setTaskMVVSelections(newSet);
  };

  const toggleCollegeExpanded = (collegeId) => {
    const newSet = new Set(expandedColleges);
    if (newSet.has(collegeId)) {
      newSet.delete(collegeId);
    } else {
      newSet.add(collegeId);
    }
    setExpandedColleges(newSet);
  };

  const toggleTaskDetails = (taskId) => {
    const newSet = new Set(expandedTaskDetails);
    if (newSet.has(taskId)) {
      newSet.delete(taskId);
    } else {
      newSet.add(taskId);
    }
    setExpandedTaskDetails(newSet);
  };

  const handleStartSprint = () => {
    if (selectedTasks.size === 0) return;

    const sprintStats = calculateSprintStats();
    const sprintData = {
      title: `Custom Sprint - ${sprintStats.taskCount} tasks`,
      tasks: sprintStats.tasks,
      estimatedMinutes: sprintStats.totalMinutes,
      batchType: 'CUSTOM'
    };

    onStartSprint?.(sprintData);
    onClose();
  };

  const applySelections = async () => {
    // Apply MVV selections to database
    const { supabase } = await import('../../lib/supabase');
    const { toast } = await import('react-hot-toast');

    try {
      // Get user ID from tasks
      const userId = tasks[0]?.user_id;
      if (!userId) throw new Error('No user ID found');

      // Update MVV selections for all colleges
      const updates = colleges.map(async (college) => {
        const shouldUseMVV = mvvSelections.has(college.id);

        return supabase
          .from('timeline_items')
          .update({ mvv_selected: shouldUseMVV })
          .eq('user_id', userId)
          .eq('college_id', college.id)
          .neq('completion_status', 'completed');
      });

      await Promise.all(updates);

      toast.success('Workload plan applied successfully!');
      onClose();
    } catch (error) {
      console.error('Error applying selections:', error);
      const { toast } = await import('react-hot-toast');
      toast.error('Failed to apply workload plan');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border-2 border-slate-900">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#26a69a] to-[#1c857e] px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {mode === 'planning' ? (
                <Calculator className="w-6 h-6 text-white" />
              ) : (
                <PlayCircle className="w-6 h-6 text-white" />
              )}
              <div>
                <h2 className="text-xl font-bold text-white">
                  {mode === 'planning' ? 'Workload Calculator' : 'Custom Sprint Builder'}
                </h2>
                <p className="text-white/90 text-sm">
                  {deadlineInfo
                    ? `${deadlineInfo.daysUntil} days until ${deadlineInfo.college} deadline`
                    : mode === 'planning'
                      ? 'Plan your application workload'
                      : 'Select tasks for your session'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setMode('planning')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                mode === 'planning'
                  ? 'bg-white text-[#26a69a]'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <Calculator className="w-4 h-4 inline mr-2" />
              Plan Workload
            </button>
            <button
              onClick={() => setMode('sprint')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                mode === 'sprint'
                  ? 'bg-white text-[#26a69a]'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <PlayCircle className="w-4 h-4 inline mr-2" />
              Build Sprint
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {mode === 'planning' ? (
            <>
              {/* PLANNING MODE */}
              {/* Available Hours Slider */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6 border-2 border-slate-200">
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              How many hours per day can you dedicate to applications?
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max="12"
                step="0.5"
                value={availableHoursPerDay}
                onChange={(e) => setAvailableHoursPerDay(parseFloat(e.target.value))}
                className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#26a69a]"
              />
              <div className="text-2xl font-bold text-[#26a69a] min-w-[80px] text-right">
                {availableHoursPerDay}h/day
              </div>
            </div>
          </div>

          {/* Results Summary */}
          {workloadResults && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className={`rounded-lg p-4 border-2 ${
                workloadResults.isFeasible
                  ? 'bg-green-50 border-green-300'
                  : 'bg-red-50 border-red-300'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {workloadResults.isFeasible ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  )}
                  <span className="text-xs font-semibold uppercase text-slate-700">
                    {workloadResults.isFeasible ? 'Feasible' : 'Too Much'}
                  </span>
                </div>
                <div className={`text-2xl font-bold ${
                  workloadResults.isFeasible ? 'text-green-700' : 'text-red-700'
                }`}>
                  {workloadResults.dailyHoursNeeded}h/day
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  {workloadResults.isFeasible ? 'You can do this!' : 'Need to reduce workload'}
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-300">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span className="text-xs font-semibold uppercase text-slate-700">Total Hours</span>
                </div>
                <div className="text-2xl font-bold text-blue-700">
                  {workloadResults.totalHours}h
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Across {selectedColleges.size} college{selectedColleges.size !== 1 ? 's' : ''}
                </p>
              </div>

              <div className={`rounded-lg p-4 border-2 ${
                workloadResults.bufferPercentage >= 20
                  ? 'bg-green-50 border-green-300'
                  : workloadResults.bufferPercentage >= 0
                    ? 'bg-yellow-50 border-yellow-300'
                    : 'bg-red-50 border-red-300'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-slate-600" />
                  <span className="text-xs font-semibold uppercase text-slate-700">Buffer</span>
                </div>
                <div className="text-2xl font-bold text-slate-700">
                  {workloadResults.bufferPercentage}%
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  {workloadResults.bufferHours >= 0 ? '+' : ''}{workloadResults.bufferHours}h cushion
                </p>
              </div>
            </div>
          )}

          {/* College Selection */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-[#26a69a]" />
              Select Colleges to Include
            </h3>

            {colleges
              .sort((a, b) => (a.priority_order || 999) - (b.priority_order || 999))
              .map((college) => {
              const isSelected = selectedColleges.has(college.id);
              const useMVV = mvvSelections.has(college.id);
              const collegeData = workloadResults?.colleges.find(c => c.college.id === college.id);
              const collegeName = college.name || college.global_colleges?.name;
              const tier = college.category;

              return (
                <div
                  key={college.id}
                  className={`rounded-lg p-4 border-2 transition-all ${
                    isSelected
                      ? 'bg-white border-[#26a69a]'
                      : 'bg-slate-50 border-slate-200 opacity-60'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCollege(college.id)}
                        className="w-5 h-5 text-[#26a69a] rounded border-gray-300 focus:ring-[#26a69a]"
                      />

                      {/* College Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-900">{collegeName}</span>
                          {tier && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              tier === 'reach' ? 'bg-red-100 text-red-800' :
                              tier === 'target' ? 'bg-blue-100 text-blue-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {tier.charAt(0).toUpperCase() + tier.slice(1)}
                            </span>
                          )}
                          {college.priority_order && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-700">
                              #{college.priority_order}
                            </span>
                          )}
                        </div>
                        {isSelected && collegeData && (
                          <div className="text-sm text-slate-600">
                            {collegeData.hours}h ({collegeData.taskCount} tasks)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recommendations */}
          {workloadResults && !workloadResults.isFeasible && (
            <div className="mt-6 bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-900 mb-2">Workload Too High - Suggestions:</h4>
                  <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                    <li>Enable MVV mode for safety schools to save time</li>
                    <li>Consider removing 1-2 schools from your list</li>
                    <li>Focus on schools with earlier deadlines first</li>
                    <li>Increase your available hours per day if possible</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {workloadResults && workloadResults.isFeasible && workloadResults.bufferPercentage >= 20 && (
            <div className="mt-6 bg-green-50 border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Great Plan!</h4>
                  <p className="text-sm text-green-800">
                    You have a {workloadResults.bufferPercentage}% buffer built in. This gives you breathing room for unexpected delays or extra polish on important applications.
                  </p>
                </div>
              </div>
            </div>
          )}
            </>
          ) : (
            <>
              {/* SPRINT BUILDER MODE */}
              {/* Sprint Stats Summary */}
              {selectedTasks.size > 0 && (() => {
                const stats = calculateSprintStats();
                return (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-300">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-5 h-5 text-blue-600" />
                        <span className="text-xs font-semibold uppercase text-slate-700">Tasks</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-700">{stats.taskCount}</div>
                      <p className="text-xs text-slate-600 mt-1">Selected for sprint</p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4 border-2 border-green-300">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-green-600" />
                        <span className="text-xs font-semibold uppercase text-slate-700">Duration</span>
                      </div>
                      <div className="text-2xl font-bold text-green-700">{stats.totalHours}h</div>
                      <p className="text-xs text-slate-600 mt-1">{stats.totalMinutes} minutes total</p>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-300">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-5 h-5 text-purple-600" />
                        <span className="text-xs font-semibold uppercase text-slate-700">Focus</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-700">
                        {stats.totalMinutes <= 120 ? 'Good' : 'Long'}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        {stats.totalMinutes <= 90 ? 'Great session length' : stats.totalMinutes <= 120 ? 'Add break time' : 'Consider splitting'}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Task Selection by College and Standard Tasks */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#26a69a]" />
                  Select Tasks for Your Sprint
                </h3>

                {/* Standard (College-Agnostic) Tasks */}
                {(() => {
                  // Use getActionableTasks to filter properly (excludes reminders, events, items without workflow_key)
                  const actionableTasks = getActionableTasks(tasks);
                  const standardTasks = actionableTasks.filter(t => !t.college_id);
                  if (standardTasks.length === 0) return null;

                  const isExpanded = expandedColleges.has('standard');
                  const selectedCount = standardTasks.filter(t => selectedTasks.has(t.id)).length;

                  return (
                    <div className="border-2 border-slate-200 rounded-lg bg-white overflow-hidden">
                      {/* Standard Tasks Header */}
                      <button
                        onClick={() => toggleCollegeExpanded('standard')}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-purple-600" />
                          <span className="font-semibold text-slate-900">General Tasks</span>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-800">
                            College Prep
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500">
                            {selectedCount}/{standardTasks.length} selected
                          </span>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </button>

                      {/* Tasks List */}
                      {isExpanded && (
                        <div className="px-4 pb-4 space-y-2 border-t border-slate-200">
                          {standardTasks.map((task) => {
                            const isSelected = selectedTasks.has(task.id);
                            const useMVV = taskMVVSelections.has(task.id);
                            const hasMVV = task.mvv_time_required && task.mvv_time_required !== task.estimated_duration;
                            const standardMinutes = task.estimated_duration || 30;
                            const mvvMinutes = task.mvv_time_required || standardMinutes;
                            const standardHours = Math.round(standardMinutes / 60 * 10) / 10;
                            const mvvHours = Math.round(mvvMinutes / 60 * 10) / 10;
                            const displayMinutes = useMVV && hasMVV ? mvvMinutes : standardMinutes;
                            const displayHours = useMVV && hasMVV ? mvvHours : standardHours;
                            const isDetailsExpanded = expandedTaskDetails.has(task.id);

                            return (
                              <div
                                key={task.id}
                                className={`rounded-lg border-2 transition-all ${
                                  isSelected
                                    ? 'bg-[#26a69a]/10 border-[#26a69a]'
                                    : 'bg-slate-50 border-slate-200'
                                }`}
                              >
                                {/* Main Task Row */}
                                <div className="p-3 flex items-center gap-3">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleTask(task.id)}
                                    className="w-5 h-5 text-[#26a69a] rounded border-gray-300 focus:ring-[#26a69a]"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-slate-900">
                                      {task.friendly_title || task.title}
                                    </div>
                                    <div className="text-xs text-slate-600 mt-1 flex items-center gap-2">
                                      <Clock className="w-3 h-3" />
                                      {hasMVV ? (
                                        useMVV ? (
                                          <span><span className="font-semibold">{mvvHours}h</span> to {standardHours}h</span>
                                        ) : (
                                          <span><span className="font-semibold">{standardHours}h</span> to {mvvHours}h</span>
                                        )
                                      ) : (
                                        <span>~{displayHours}h</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* MVV/Optimal Toggle */}
                                  {hasMVV && (
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => toggleTaskDetails(task.id)}
                                        className="p-1 hover:bg-slate-200 rounded transition-colors"
                                        title="Show details"
                                      >
                                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isDetailsExpanded ? 'rotate-180' : ''}`} />
                                      </button>
                                      <button
                                        onClick={() => toggleTaskMVV(task.id)}
                                        className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                                          useMVV
                                            ? 'bg-orange-500 text-white shadow-sm'
                                            : 'border border-slate-300 text-slate-400 hover:text-slate-600 hover:border-slate-400'
                                        }`}
                                      >
                                        <Zap className="w-3 h-3 inline mr-1" />
                                        {useMVV ? 'MVV' : 'Optimal'}
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* Expanded Details */}
                                {hasMVV && isDetailsExpanded && (
                                  <div className="px-3 pb-3 pt-0 space-y-2 border-t border-slate-200 mt-2">
                                    {/* Optimal Option */}
                                    <button
                                      onClick={() => useMVV && toggleTaskMVV(task.id)}
                                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                        !useMVV
                                          ? 'border-[#26a69a] bg-[#26a69a]/5'
                                          : 'border-slate-200 hover:border-slate-300'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                            <CheckCircle2 className={`w-4 h-4 ${!useMVV ? 'text-[#26a69a]' : 'text-slate-300'}`} />
                                            <span className="font-semibold text-sm">Optimal Version</span>
                                            <span className="text-xs text-slate-500">{standardHours}h</span>
                                          </div>
                                          <p className="text-xs text-slate-600 ml-6">
                                            {task.description || 'Complete version with all requirements'}
                                          </p>
                                        </div>
                                      </div>
                                    </button>

                                    {/* MVV Option */}
                                    <button
                                      onClick={() => !useMVV && toggleTaskMVV(task.id)}
                                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                        useMVV
                                          ? 'border-orange-500 bg-orange-50'
                                          : 'border-slate-200 hover:border-slate-300'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                            <Zap className={`w-4 h-4 ${useMVV ? 'text-orange-600' : 'text-slate-300'}`} />
                                            <span className="font-semibold text-sm">MVV (Faster)</span>
                                            <span className="text-xs text-slate-500">{mvvHours}h</span>
                                          </div>
                                          <p className="text-xs text-slate-600 ml-6">
                                            {task.min_viable_version || 'Streamlined version focusing on essentials'}
                                          </p>
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* College-Specific Tasks (Sorted by Strategic Order) */}
                {(() => {
                  // Use getActionableTasks to filter properly (excludes reminders, events, items without workflow_key)
                  const actionableTasks = getActionableTasks(tasks);

                  return colleges
                    .sort((a, b) => (a.priority_order || 999) - (b.priority_order || 999))
                    .map((college) => {
                      const collegeName = college.name || college.global_colleges?.name;
                      const tier = college.category;
                      const collegeTasks = actionableTasks.filter(t => t.college_id === college.id);

                      if (collegeTasks.length === 0) return null;

                    const isExpanded = expandedColleges.has(college.id);
                    const selectedCount = collegeTasks.filter(t => selectedTasks.has(t.id)).length;

                    // Calculate total workflow time for this college
                    const workflowMinutes = collegeTasks.reduce((sum, task) => {
                      const useMVV = taskMVVSelections.has(task.id);
                      const hasMVV = task.mvv_time_required && task.mvv_time_required !== task.estimated_duration;
                      const duration = useMVV && hasMVV ? task.mvv_time_required : (task.estimated_duration || 30);
                      return sum + duration;
                    }, 0);
                    const workflowHours = Math.round(workflowMinutes / 60 * 10) / 10;

                    // Get deadline from global_colleges based on application_round
                    const getDeadline = () => {
                      const gc = college.global_colleges;
                      if (!gc) return null;

                      switch (college.application_round) {
                        case 'early_decision': return gc.ed_deadline;
                        case 'early_decision_2': return gc.ed2_deadline;
                        case 'early_action':
                        case 'restrictive_early_action':
                        case 'single_choice_early_action': return gc.ea_deadline;
                        case 'regular':
                        case 'rolling':
                        case 'priority':
                        default: return gc.rd_deadline;
                      }
                    };

                    const deadline = getDeadline();
                    const deadlineText = deadline ? new Date(deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;

                    return (
                      <div key={college.id} className="border-2 border-slate-200 rounded-lg bg-white overflow-hidden">
                        {/* College Header */}
                        <button
                          onClick={() => toggleCollegeExpanded(college.id)}
                          className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900">{collegeName}</span>
                              {tier && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  tier === 'reach' ? 'bg-red-100 text-red-800' :
                                  tier === 'target' ? 'bg-blue-100 text-blue-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                                </span>
                              )}
                              {college.priority_order && (
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-700">
                                  #{college.priority_order}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-slate-600 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {workflowHours}h total
                              </span>
                              {deadlineText && (
                                <>
                                  <span className="text-slate-400">•</span>
                                  <span className="text-xs text-slate-600 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Due {deadlineText}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-slate-500">
                              {selectedCount}/{collegeTasks.length} selected
                            </span>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </button>

                        {/* Tasks List */}
                        {isExpanded && (
                          <div className="px-4 pb-4 space-y-2 border-t border-slate-200">
                            {collegeTasks.map((task) => {
                              const isSelected = selectedTasks.has(task.id);
                              const useMVV = taskMVVSelections.has(task.id);
                              const hasMVV = task.mvv_time_required && task.mvv_time_required !== task.estimated_duration;
                              const standardMinutes = task.estimated_duration || 30;
                              const mvvMinutes = task.mvv_time_required || standardMinutes;
                              const standardHours = Math.round(standardMinutes / 60 * 10) / 10;
                              const mvvHours = Math.round(mvvMinutes / 60 * 10) / 10;
                              const displayMinutes = useMVV && hasMVV ? mvvMinutes : standardMinutes;
                              const displayHours = useMVV && hasMVV ? mvvHours : standardHours;
                              const isDetailsExpanded = expandedTaskDetails.has(task.id);

                              return (
                                <div
                                  key={task.id}
                                  className={`rounded-lg border-2 transition-all ${
                                    isSelected
                                      ? 'bg-[#26a69a]/10 border-[#26a69a]'
                                      : 'bg-slate-50 border-slate-200'
                                  }`}
                                >
                                  {/* Main Task Row */}
                                  <div className="p-3 flex items-center gap-3">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleTask(task.id)}
                                      className="w-5 h-5 text-[#26a69a] rounded border-gray-300 focus:ring-[#26a69a]"
                                    />
                                    <div className="flex-1">
                                      <div className="font-medium text-slate-900">
                                        {task.friendly_title || task.title}
                                      </div>
                                      <div className="text-xs text-slate-600 mt-1 flex items-center gap-2">
                                        <Clock className="w-3 h-3" />
                                        {hasMVV ? (
                                          useMVV ? (
                                            <span><span className="font-semibold">{mvvHours}h</span> to {standardHours}h</span>
                                          ) : (
                                            <span><span className="font-semibold">{standardHours}h</span> to {mvvHours}h</span>
                                          )
                                        ) : (
                                          <span>~{displayHours}h</span>
                                        )}
                                        {task.category && (
                                          <>
                                            <span className="text-slate-400">•</span>
                                            <span className="capitalize">{task.category}</span>
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    {/* MVV/Optimal Toggle */}
                                    {hasMVV && (
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => toggleTaskDetails(task.id)}
                                          className="p-1 hover:bg-slate-200 rounded transition-colors"
                                          title="Show details"
                                        >
                                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isDetailsExpanded ? 'rotate-180' : ''}`} />
                                        </button>
                                        <button
                                          onClick={() => toggleTaskMVV(task.id)}
                                          className={`px-2 py-1 rounded text-xs font-semibold transition-all ${
                                            useMVV
                                              ? 'bg-orange-500 text-white shadow-sm'
                                              : 'border border-slate-300 text-slate-400 hover:text-slate-600 hover:border-slate-400'
                                          }`}
                                        >
                                          <Zap className="w-3 h-3 inline mr-1" />
                                          {useMVV ? 'MVV' : 'Optimal'}
                                        </button>
                                      </div>
                                    )}
                                  </div>

                                  {/* Expanded Details */}
                                  {hasMVV && isDetailsExpanded && (
                                    <div className="px-3 pb-3 pt-0 space-y-2 border-t border-slate-200 mt-2">
                                      {/* Optimal Option */}
                                      <button
                                        onClick={() => useMVV && toggleTaskMVV(task.id)}
                                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                          !useMVV
                                            ? 'border-[#26a69a] bg-[#26a69a]/5'
                                            : 'border-slate-200 hover:border-slate-300'
                                        }`}
                                      >
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <CheckCircle2 className={`w-4 h-4 ${!useMVV ? 'text-[#26a69a]' : 'text-slate-300'}`} />
                                              <span className="font-semibold text-sm">Optimal Version</span>
                                              <span className="text-xs text-slate-500">{standardHours}h</span>
                                            </div>
                                            <p className="text-xs text-slate-600 ml-6">
                                              {task.description || 'Complete version with all requirements'}
                                            </p>
                                          </div>
                                        </div>
                                      </button>

                                      {/* MVV Option */}
                                      <button
                                        onClick={() => !useMVV && toggleTaskMVV(task.id)}
                                        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                          useMVV
                                            ? 'border-orange-500 bg-orange-50'
                                            : 'border-slate-200 hover:border-slate-300'
                                        }`}
                                      >
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <Zap className={`w-4 h-4 ${useMVV ? 'text-orange-600' : 'text-slate-300'}`} />
                                              <span className="font-semibold text-sm">MVV (Faster)</span>
                                              <span className="text-xs text-slate-500">{mvvHours}h</span>
                                            </div>
                                            <p className="text-xs text-slate-600 ml-6">
                                              {task.min_viable_version || 'Streamlined version focusing on essentials'}
                                            </p>
                                          </div>
                                        </div>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Empty State */}
              {selectedTasks.size === 0 && (
                <div className="mt-6 bg-blue-50 border-2 border-blue-300 rounded-lg p-6 text-center">
                  <PlayCircle className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-blue-900 mb-2">Build Your Custom Sprint</h4>
                  <p className="text-sm text-blue-800">
                    Select tasks from the list above to create a focused work session. We'll track your time and progress!
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t-2 border-slate-200 px-6 py-4 bg-slate-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          {mode === 'planning' ? (
            <button
              onClick={applySelections}
              disabled={!workloadResults}
              className="px-6 py-2 bg-[#26a69a] hover:bg-[#1c857e] text-white rounded-lg font-semibold border-2 border-slate-900 transition-all hover:-translate-y-0.5 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none"
            >
              Apply This Plan
            </button>
          ) : (
            <button
              onClick={handleStartSprint}
              disabled={selectedTasks.size === 0}
              className="px-6 py-2 bg-[#26a69a] hover:bg-[#1c857e] text-white rounded-lg font-semibold border-2 border-slate-900 transition-all hover:-translate-y-0.5 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
            >
              <PlayCircle className="w-5 h-5" />
              Start Sprint ({selectedTasks.size} task{selectedTasks.size !== 1 ? 's' : ''})
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkloadCalculatorModal;
